import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Alert, Row, Col, FormLabel } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import TextLoading from "../../global/TextLoading";

import { CreateGroups } from "../../../api/Cycles";

const customStyles = {
  falseInput: {
    backgroundColor: "#ffffff",
    minHeight: "38px",
    display: "flex",
    alignItems: "center",
    border: "1px solid #ced4da",
    borderRadius: "0.25rem",
    color: "#ced4da",
    fontSize: "1rem",
  },
  addIcon: {
    color: "#247048",
    cursor: "pointer",
    marginLeft: "4px",
    fontSize: "17px",
  },
  removeIcon: {
    cursor: "pointer",
    marginLeft: "4px",
    fontSize: "17px",
  },
};

export default function ModalAddGroups(props) {
  const [t] = useTranslation(["global", "class"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [gradeSelected, setGradeSelected] = useState(() =>
    JSON.parse(JSON.stringify(props.gradeSelected)),
  );

  const addNewGroup = () => {
    // Avoid more than 26 groups (A-Z)
    if (gradeSelected.groups.length === 26) return;

    // Copia profunda del estado actual
    const newGradeSelected = JSON.parse(JSON.stringify(gradeSelected));
    const lastGroup = newGradeSelected.groups.at(-1).label;
    const nextGroup = String.fromCharCode(lastGroup.charCodeAt(0) + 1);

    newGradeSelected.groups.push({
      id: "new",
      label: nextGroup,
    });
    setGradeSelected(newGradeSelected);
  };

  const removeGroup = (index) => {
    const newGradeSelected = JSON.parse(JSON.stringify(gradeSelected));
    newGradeSelected.groups.splice(index, 1);
    setGradeSelected(newGradeSelected);
  };

  const submitForm = async () => {
    setIsSubmitting(true);
    const payload = {
      school_cycle_id: props.data.schoolCycleId,
      organization_school_level_id: props.data.organizationSchoolLevelId,
      grade_level_id: gradeSelected.id,
      grade_groups: gradeSelected.groups.length,
    };

    CreateGroups(payload).then((response) => {
      if (response.status) {
        const levels = response.data;
        const level = levels.find(
          (lvl) => lvl.id == props.data.organizationSchoolLevelId,
        );
        const grades = obtainGradesAndGroups(level);
        props.setGradesList(grades);
        const grade = grades.find((g) => g.id == gradeSelected.id);
        if (grade.groups.length > 1 && grade.groups[0].id !== 0) {
          const allGroupsOption = {
            id: 0,
            value: 0,
            label: t("class:classDetails.allGroups"),
            gggl: null,
          };
          grade.groups.unshift(allGroupsOption);
        }
        props.setGroupsList(grade.groups);
        cleanAndClose();
      }
      setIsSubmitting(false);
    });
  };

  const obtainGradesAndGroups = (level) => {
    const gradesActive = level.grades.filter((grade) => grade.active == 1);
    let grades = gradesActive.map((grade) => ({
      id: grade.id,
      value: grade.id,
      label: grade.grade_level,
      groups: grade.groups
        .filter((group) => group.active == 1)
        .map((group) => ({
          id: group.id,
          value: group.id,
          label: group.name,
        })),
    }));
    return grades;
  };

  const cleanAndClose = () => {
    setError("");
    setIsSubmitting(false);
    props.setShowModalAddGroup(false);
  };

  return (
    <Modal
      show={props.showModalAddGroup}
      aria-labelledby="contained-modal-title-lg"
      id="modalAddGroup"
    >
      <Modal.Header>
        <Modal.Title className="modal-title">
          {t("class:modalAddGroup.title")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Headers */}
        <Row>
          <Col md={6}>
            <FormLabel>{t("class:modalAddGroup.gradeLabel")}</FormLabel>
          </Col>
          <Col md={5}>
            <FormLabel>{t("class:modalAddGroup.groupsLabel")}</FormLabel>
          </Col>
          <Col md={1}>
            <i
              className="bi bi-plus-circle-fill"
              id="addGroup"
              role="button"
              style={customStyles.addIcon}
              onClick={() => addNewGroup()}
            />
          </Col>
        </Row>
        {/* Groups */}
        {gradeSelected.groups.map((item, index) => {
          return (
            <Row key={index} className="mb-2">
              <Col sm={6} md={6} lg={6} hidden={index > 0}>
                <div
                  id={"grade-" + index}
                  className="form-control"
                  style={customStyles.falseInput}
                >
                  {gradeSelected.label}
                </div>
              </Col>
              <Col
                sm={5}
                md={5}
                lg={5}
                className={index > 0 ? "offset-lg-6" : ""}
              >
                <div
                  id={"group-" + index}
                  className="form-control"
                  style={customStyles.falseInput}
                >
                  {item.label}
                </div>
              </Col>
              {item.id === "new" && (
                <Col md={1} className="pt-2">
                  <i
                    className="bi bi-trash"
                    id="removeGroup"
                    role="button"
                    style={customStyles.removeIcon}
                    onClick={() => removeGroup(index)}
                  />
                </Col>
              )}
            </Row>
          );
        })}

        {/** Show error message */}
        {error && (
          <Row>
            <Col md={12} className="mt-3">
              <Alert variant="warning">{error}</Alert>
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          id="cancelAddGroup"
          variant="outline-secondary"
          onClick={() => cleanAndClose()}
          disabled={isSubmitting}
        >
          {t("global:buttons.cancel")}
        </Button>
        <Button
          id="saveAddGroup"
          type="submit"
          disabled={isSubmitting}
          onClick={() => submitForm()}
        >
          {isSubmitting ? (
            <TextLoading text={t("global:buttons.saving")} variant="light" />
          ) : (
            t("global:buttons.save")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

ModalAddGroups.propTypes = {
  showModalAddGroup: PropTypes.bool, //Required
  setShowModalAddGroup: PropTypes.func, //Required
  gradeSelected: PropTypes.object,
  data: PropTypes.object,
  setGradesList: PropTypes.func,
  setGroupsList: PropTypes.func,
};
